import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  changeFinishedOrdersPeriod,
  changeOrderFilterByExpert,
  changeOrdersPage,
  changeOrdersPageSize,
  fetchOrdersRequest,
  ordersChangeFilter,
  resetOrdersPage,
} from 'store/orders/orders.actions'
import { selectOrderFilterByExpert, selectOrdersPage, selectOrdersPageSize } from 'store/orders/orders.selectors'
import { selectCurrentUser } from 'store/users/users.selectors'
import { UserRole } from 'types/user'
import { usePaginationLocation } from 'components/molecules/PaginationBar/pagination-location-hook'
import { useOrderListFilterByExpertLocation } from 'components/molecules/OrderListFilterByExpert/order-list-filter-by-expert-location-hook'
import { isSuperExpert } from 'utils/super-expert'
import { useOrderListFilterLocation } from './order-list-filter-location-hook'
import { useOrderListFinishedPeriodLocation } from './order-list-finished-period-location-hook'

export const useOrderListFilter = (userRole: UserRole) => {
  const dispatch = useDispatch()
  const currentUser = useSelector(selectCurrentUser)
  const currentPage = useSelector(selectOrdersPage)
  const currentPageSize = useSelector(selectOrdersPageSize)
  const currentExpertFilter = useSelector(selectOrderFilterByExpert)
  const storage = useOrderListFilterLocation(userRole)
  const paginationLocation = usePaginationLocation()
  const expertLocation = useOrderListFilterByExpertLocation()
  const finishedPeriodLocation = useOrderListFinishedPeriodLocation()
  const [value, setValue] = useState(() => storage.get())

  const syncOrdersPeriod = useCallback(() => {
    const period = finishedPeriodLocation.ensureDefaultInUrl()
    dispatch(changeFinishedOrdersPeriod(period))
    return period
  }, [dispatch, finishedPeriodLocation])

  const onChange = useCallback(
    (newValue: string) => {
      storage.set(newValue)
      setValue(newValue)
      dispatch(ordersChangeFilter(newValue))
      const period = syncOrdersPeriod()

      paginationLocation.setPage(1)
      dispatch(resetOrdersPage())
      dispatch(fetchOrdersRequest({ period }))
    },
    [dispatch, paginationLocation, storage, syncOrdersPeriod],
  )

  useEffect(() => {
    const initialValue = storage.get()
    setValue(initialValue)
    dispatch(ordersChangeFilter(initialValue))
    const period = syncOrdersPeriod()

    paginationLocation.ensureInUrl()
    const { page, pageSize } = paginationLocation.getFromUrl()

    if (currentPage !== page) {
      dispatch(changeOrdersPage(page))
    }

    if (currentPageSize !== pageSize) {
      dispatch(changeOrdersPageSize(pageSize))
    }

    if (isSuperExpert(currentUser?.id)) {
      const expert = expertLocation.get()
      if (currentExpertFilter !== expert) {
        dispatch(changeOrderFilterByExpert(expert))
      }
    }

    dispatch(fetchOrdersRequest({ period }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, dispatch, storage])

  return {
    value,
    onChange,
  }
}
